import { Component, OnInit } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Observable } from 'rxjs';
import { CommonService } from '../../services/common.service';
import { EndCustomerRow, InvOrgRow, SubAreaRow } from '../../models/common.model';
import { AuthService } from 'src/app/core/services/auth.service';

export type LookupDialogType = 'subArea' | 'endCustomer' | 'invOrg';

export interface LookupColumn {
  header: string;
  field: string;
}

@Component({
  selector: 'app-sales-cost-lookup-dialog',
  templateUrl: './sales-cost-lookup-dialog.component.html',
  styleUrls: ['./sales-cost-lookup-dialog.component.scss'],
})
export class SalesCostLookupDialogComponent implements OnInit {
  searchKeyword: string = '';
  tableData: any[] = [];
  selectedRow: any = null;
  loading = false;
  columns: LookupColumn[] = [];
  dataKey = 'id';

  private type!: LookupDialogType;
  private ouId: number = 0;
  private brand: string = '';
  private empNo: string = '';

  constructor(
    private readonly ref: DynamicDialogRef,
    private readonly config: DynamicDialogConfig,
    private readonly commonService: CommonService,
    private readonly authService: AuthService
  ) {}

  ngOnInit(): void {
    const data = this.config.data;
    this.type = data.type;
    this.ouId = data.ouId ?? 0;
    this.brand = data.brand ?? '';
    this.empNo = data.empNo ?? this.authService.getUser()?.username ?? '';

    switch (this.type) {
      case 'subArea':
        this.columns = [
          { header: 'Sub Area', field: 'subArea' },
          { header: 'Area', field: 'area' },
          { header: 'Description', field: 'description' },
        ];
        this.dataKey = 'subArea';
        break;
      case 'endCustomer':
        this.columns = [
          { header: 'End Customer', field: 'endCustomer' },
          { header: 'Brand', field: 'brand' }
        ];
        this.dataKey = 'endCustomer';
        break;
      case 'invOrg':
        this.columns = [
          { header: 'Organization Code', field: 'organizationCode' },
          { header: 'Organization Name', field: 'organizationName' },
        ];
        this.dataKey = 'organizationCode';
        break;
    }

    this.loadData();
  }

  get filteredData(): any[] {
    if (!this.searchKeyword) return this.tableData;
    const kw = this.searchKeyword.toLowerCase();
    return this.tableData.filter(row =>
      this.columns.some(col => (row[col.field] ?? '').toString().toLowerCase().includes(kw))
    );
  }

  loadData(): void {
    this.loading = true;
    this.selectedRow = null;

    let search$: Observable<SubAreaRow[] | EndCustomerRow[] | InvOrgRow[]>;

    if (this.type === 'subArea') {
      search$ = this.commonService.getSubAreas(this.ouId, this.empNo);
    } else if (this.type === 'endCustomer') {
      search$ = this.commonService.getEndCustomers(this.ouId, this.brand);
    } else {
      search$ = this.commonService.getInvOrgs(this.ouId);
    }

    search$.subscribe({
      next: (data) => {
        if (this.type === 'endCustomer') {
          this.tableData = (data as EndCustomerRow[]).map(row => ({ ...row, brand: this.brand }));
        } else {
          this.tableData = data;
        }
        this.loading = false;
      },
      error: () => {
        this.tableData = [];
        this.loading = false;
      },
    });
  }

  onRowDblClick(row: any): void {
    this.selectedRow = row;
    this.onOk();
  }

  onOk(): void {
    this.ref.close(this.selectedRow ?? null);
  }

  onClear(): void {
    if (this.type === 'subArea') {
      this.ref.close({ subArea: '', area: '' });
    } else if (this.type === 'endCustomer') {
      this.ref.close({ endCustomer: '' });
    } else {
      this.ref.close({ organizationCode: '' });
    }
  }

  onCancel(): void {
    this.ref.close(null);
  }
}
